import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/it';
import { CalendarioUtenteGiorno } from '../../../interfaces/CalendarioUtenteGiorno';
import { UserInterface } from '../../../interfaces/User';
import { capitalizeFirstLetter } from '../../../utility_objects/CapitalizeFirstLetter';
import { AssenzaOperatore, TipoAssenza } from './AssenzeOperatoriUtils';
import {
  FasceOrarie,
  formatFasceOrarie,
  isOrarioNegozio,
} from './FasceOrarieUtils';
import { buildInServizio, UtenteInServizio } from './InServizioUtils';

/**
 * Fascia Presenze dell'agenda negozio: per ogni giorno visibile chi è in servizio
 * (con le fasce orarie) e chi è assente, tutto il giorno o solo in parte.
 */

/** Persona in servizio nel giorno; `assenzaParziale` = è assente solo in parte. */
export interface PersonaInServizio extends UtenteInServizio {
  assenzaParziale: TipoAssenza | null;
}

/** Assenza che tocca il giorno, una sola per persona. */
export interface AssenteDelGiorno extends AssenzaOperatore {
  /** true = assente tutto il giorno (chip tinta); false = in servizio in parte. */
  tuttoIlGiorno: boolean;
}

export interface PresenzeGiorno {
  /** YYYY-MM-DD */
  giorno: string;
  /**
   * Ordinati per ora d'inizio e poi per username; null = dati "in servizio" del
   * giorno non disponibili.
   */
  inServizio: PersonaInServizio[] | null;
  /** Prima le giornate intere, poi le parziali; null = assenze non disponibili. */
  assenti: AssenteDelGiorno[] | null;
}

const FORMATO_DATA = 'YYYY-MM-DD';

/**
 * Giorni coperti dalla vista dell'agenda, con la settimana da lunedì
 * (firstDayOfWeek 1 dello Scheduler):
 * - Giorno e Agenda: il giorno selezionato (l'Agenda della console ne carica uno);
 * - 3 Giorni: dal selezionato ai due successivi, anche a cavallo di due settimane;
 * - Settimana e Lavorativo: da lunedì a domenica (i giorni chiusi restano, la cella
 *   è vuota).
 * Nel Mese la fascia non c'è: nessun giorno, quindi nessuna chiamata.
 */
export const getGiorniVisibili = (
  view: string,
  selectedDate: Date | string
): string[] => {
  const giorno = dayjs(selectedDate).locale('it').startOf('day');
  const daGiorno = (inizio: Dayjs, quanti: number): string[] =>
    Array.from({ length: quanti }, (_, i) =>
      inizio.add(i, 'day').format(FORMATO_DATA)
    );
  switch (view) {
    case 'Month':
      return [];
    case '3Days':
      return daGiorno(giorno, 3);
    case 'Week':
    case 'WorkWeek':
      return daGiorno(giorno.startOf('week'), 7);
    default:
      return [giorno.format(FORMATO_DATA)];
  }
};

// Colori dei tipi di Turni e orari (la Rota di FOCUS CLOUD, UserDayCell e
// LegendaUser): da tenere allineati a quelli. Malattia è --accent-pressed-color,
// #de5c5c in tutti i temi. Valori scritti qui e non in un CSS: gli spec non caricano
// i CSS, e la tinta dello sfondo si ottiene col suffisso alfa (getColoriChip).
export const COLORE_TIPO: Record<TipoAssenza, string> = {
  Ferie: '#ffd300',
  Permesso: '#e77d22',
  Malattia: '#de5c5c',
  Altro: '#1375c0',
};

// Ordine delle chip a parità di giornata intera o parziale: prima la più grave.
const ORDINE_TIPO: Record<TipoAssenza, number> = {
  Malattia: 0,
  Ferie: 1,
  Permesso: 2,
  Altro: 3,
};

/** Prima le giornate intere, poi le parziali; poi per tipo e per username. */
export const confrontaAssenti = (
  a: AssenteDelGiorno,
  b: AssenteDelGiorno
): number =>
  Number(b.tuttoIlGiorno) - Number(a.tuttoIlGiorno) ||
  ORDINE_TIPO[a.tipo] - ORDINE_TIPO[b.tipo] ||
  a.username.localeCompare(b.username);

// Assenze che toccano il giorno, una per persona: se le righe si sovrappongono
// vince la giornata intera, poi il tipo più grave.
const getAssentiDelGiorno = (
  assenze: AssenzaOperatore[],
  giorno: string
): AssenteDelGiorno[] => {
  const perUtente = new Map<string, AssenteDelGiorno>();
  for (const assenza of assenze) {
    if (assenza.dal > giorno || assenza.al < giorno) continue;
    const assente = { ...assenza, tuttoIlGiorno: assenza.orario === null };
    const presente = perUtente.get(assente.userId);
    if (!presente || confrontaAssenti(assente, presente) < 0)
      perUtente.set(assente.userId, assente);
  }
  return [...perUtente.values()].sort(confrontaAssenti);
};

/**
 * Unisce, giorno per giorno, chi è in servizio (risposta di GetAll del giorno,
 * cercata per data esatta) e le assenze lette nella finestra. Se i dati non
 * tornano (orari in servizio e assenza di tutto il giorno) vince l'assenza.
 */
export const buildPresenzeGiorni = (
  giorni: string[],
  inServizioPerGiorno: Record<string, CalendarioUtenteGiorno[] | undefined>,
  assenze: AssenzaOperatore[] | undefined,
  users: Record<string, UserInterface>
): PresenzeGiorno[] =>
  giorni.map((giorno) => {
    const assenti = assenze ? getAssentiDelGiorno(assenze, giorno) : null;
    const assentePerUtente = new Map(
      (assenti ?? []).map((a) => [a.userId, a] as const)
    );
    const righe = inServizioPerGiorno[giorno];
    const inServizio = righe
      ? buildInServizio(righe, users, giorno)
          .filter(
            (utente) => !assentePerUtente.get(utente.userId)?.tuttoIlGiorno
          )
          .map((utente) => ({
            ...utente,
            assenzaParziale: assentePerUtente.get(utente.userId)?.tipo ?? null,
          }))
      : null;
    return { giorno, inServizio, assenti };
  });

const formatGiorno = (giorno: string, formato: string): string =>
  dayjs(giorno).locale('it').format(formato);

// I nomi del giorno che si vedono hanno l'iniziale maiuscola, come l'intestazione
// dei giorni dell'agenda. Stessi testi di FOCUS CLOUD.

/** Titolo del popover della cella, es. «Sab 18/10». */
export const formatTitoloGiorno = (giorno: string): string =>
  capitalizeFirstLetter(formatGiorno(giorno, 'ddd DD/MM'));

/** Data in testa alla cella dell'Agenda (senza intestazione dei giorni), es. «Sab 18». */
export const formatGiornoCorto = (giorno: string): string =>
  capitalizeFirstLetter(formatGiorno(giorno, 'ddd D'));

/** Giorno per esteso dei nomi accessibili, es. «sabato 18 ottobre». */
export const formatGiornoEsteso = (giorno: string): string =>
  formatGiorno(giorno, 'dddd D MMMM');

/** Sotto questa larghezza (giorni chiusi a 0px in Lavorativo) la cella è vuota. */
export const LARGHEZZA_MINIMA_CELLA = 24;

/**
 * Aspetto della chip di una persona: in servizio (senza colore), in servizio in
 * parte (bordo del colore del tipo), assente tutto il giorno (bordo e sfondo).
 */
export type StatoChip = 'inServizio' | 'inParte' | 'assente';

/**
 * Testo delle chip, secondo la modalità della fascia: in «Presenze» il solo
 * username (lo stato si legge dal colore della chip), in «Presenze totali» il testo
 * completo di formatChipPresenza.
 */
export type TestoChip = 'breve' | 'completo';

/** Chip di una persona nella cella della fascia. */
export interface ChipPersona {
  userId: string;
  stato: StatoChip;
  /** Tipo dell'assenza; null = in servizio senza assenze. */
  tipo: TipoAssenza | null;
  label: string;
}

/**
 * Colori della chip, come in Turni e orari (colore pieno per il bordo, tinta chiara
 * per lo sfondo): in servizio nessun colore (bordo del tema, `bordo` undefined); in
 * parte il bordo del tipo; assente tutto il giorno anche lo sfondo, al 25% (suffisso
 * alfa 40, letto anche dai browser incorporati di FOCUS 10).
 */
export const getColoriChip = (
  stato: StatoChip,
  tipo: TipoAssenza | null
): { bordo: string | undefined; sfondo: string } => {
  if (stato === 'inServizio' || tipo === null)
    return { bordo: undefined, sfondo: 'transparent' };
  const colore = COLORE_TIPO[tipo];
  return {
    bordo: colore,
    sfondo: stato === 'assente' ? `${colore}40` : 'transparent',
  };
};

/**
 * Testo completo della chip di una persona («Presenze totali»), uguale in tutte le
 * viste e in FOCUS CLOUD:
 * - in servizio con le fasce del negozio (`comeNegozio`, vedi isOrarioNegozio):
 *   «mrossi - Orario negozio»;
 * - in servizio con altre fasce: «mrossi - Lavoro 09:00-13:00 / 15:00-19:00»;
 * - in servizio in parte: «mrossi - Permesso, lavoro 09:00-13:00» (l'orario è
 *   quello in cui la persona c'è, ed è sempre scritto);
 * - assente tutto il giorno: «mrossi - Ferie».
 * `orario` sono le fasce di presenza già formattate; null = assente tutto il giorno.
 */
export const formatChipPresenza = ({
  username,
  tipo,
  orario,
  comeNegozio = false,
}: {
  username: string;
  tipo: TipoAssenza | null;
  orario: string | null;
  comeNegozio?: boolean;
}): string => {
  if (orario === null) return tipo ? `${username} - ${tipo}` : username;
  if (tipo) return `${username} - ${tipo}, lavoro ${orario}`;
  return comeNegozio
    ? `${username} - Orario negozio`
    : `${username} - Lavoro ${orario}`;
};

// Fra le fasce orarie di una chip.
const SEPARATORE_FASCE = ' / ';

/** Chip di chi è in servizio, anche solo in parte (allora col tipo dell'assenza). */
const getChipInServizio = (
  persona: PersonaInServizio,
  testo: TestoChip,
  orarioNegozio: FasceOrarie | null
): ChipPersona => ({
  userId: persona.userId,
  stato: persona.assenzaParziale ? 'inParte' : 'inServizio',
  tipo: persona.assenzaParziale,
  label:
    testo === 'breve'
      ? persona.username
      : formatChipPresenza({
          username: persona.username,
          tipo: persona.assenzaParziale,
          orario:
            formatFasceOrarie(persona.fasce, SEPARATORE_FASCE) ??
            persona.orario,
          comeNegozio: isOrarioNegozio(persona.fasce, orarioNegozio),
        }),
});

/**
 * Chip di un assente elencato a parte (vedi getAssentiDaElencare): tutto il giorno,
 * oppure in parte con l'orario di presenza della sua assenza.
 */
const getChipAssente = (
  assente: AssenteDelGiorno,
  testo: TestoChip
): ChipPersona => ({
  userId: assente.userId,
  stato: assente.tuttoIlGiorno ? 'assente' : 'inParte',
  tipo: assente.tipo,
  label:
    testo === 'breve'
      ? assente.username
      : formatChipPresenza({
          username: assente.username,
          tipo: assente.tipo,
          // L'orario delle assenze separa le fasce con ', ' (formatFasceOrarie).
          orario: assente.orario?.split(', ').join(SEPARATORE_FASCE) ?? null,
        }),
});

/**
 * Assenti da elencare a parte nel popover: chi manca tutto il giorno e chi è assente
 * solo in parte ma non compare fra gli in servizio ("in servizio" del giorno in
 * caricamento o in errore, oppure nessuna fascia per lui nella risposta). La cella
 * della fascia mette questi ultimi fra gli in servizio (getChipPresenze).
 * Chi è fra gli in servizio porta già il tipo con sé, così ogni persona compare una
 * volta sola. Ordine di confrontaAssenti: prima le giornate intere per gravità.
 */
export const getAssentiDaElencare = ({
  inServizio,
  assenti,
}: Pick<PresenzeGiorno, 'inServizio' | 'assenti'>): AssenteDelGiorno[] => {
  const inServizioIds = new Set(
    (inServizio ?? []).map((persona) => persona.userId)
  );
  return (assenti ?? []).filter(
    (assente) => assente.tuttoIlGiorno || !inServizioIds.has(assente.userId)
  );
};

interface ChipDaOrdinare {
  /** 'HH:mm' della prima fascia di presenza. */
  inizio: string;
  username: string;
  chip: ChipPersona;
}

/**
 * Chip della cella di un giorno, una per persona e sempre tutte, nelle due modalità
 * della fascia (come in FOCUS CLOUD):
 * - `inServizio`: chi è in servizio, anche solo in parte, per ora d'inizio e poi
 *   username. Comprende chi è assente in parte ma non compare fra gli in servizio
 *   (getAssentiDaElencare): c'è comunque negli orari della sua assenza;
 * - `assenti`: chi manca tutto il giorno, per gravità e username (confrontaAssenti).
 * `testo` sceglie il testo delle chip (TestoChip); `orarioNegozio` sono le fasce del
 * punto vendita nel giorno, per «Orario negozio» (null = non disponibili).
 */
export const getChipPresenze = (
  giorno: Pick<PresenzeGiorno, 'inServizio' | 'assenti'>,
  testo: TestoChip,
  orarioNegozio: FasceOrarie | null = null
): { inServizio: ChipPersona[]; assenti: ChipPersona[] } => {
  const daElencare = getAssentiDaElencare(giorno);
  const inServizio: ChipDaOrdinare[] = [
    ...(giorno.inServizio ?? []).map((persona) => ({
      inizio: persona.inizio,
      username: persona.username,
      chip: getChipInServizio(persona, testo, orarioNegozio),
    })),
    ...daElencare
      .filter((assente) => !assente.tuttoIlGiorno)
      .map((assente) => ({
        inizio: assente.orario?.slice(0, 5) ?? '',
        username: assente.username,
        chip: getChipAssente(assente, testo),
      })),
  ];
  return {
    inServizio: inServizio
      .sort(
        (a, b) =>
          a.inizio.localeCompare(b.inizio) ||
          a.username.localeCompare(b.username)
      )
      .map(({ chip }) => chip),
    assenti: daElencare
      .filter((assente) => assente.tuttoIlGiorno)
      .map((assente) => getChipAssente(assente, testo)),
  };
};
